import IConfig, { SubscriptionType } from '../../Display/IConfig';
import { createProgressiveWait } from '@signageos/lib/dist/Timer/progressiveWait';
import wait from '@signageos/lib/dist/Timer/wait';
import { ISignedResponse, isConfigResponseVerified } from './deviceConfigurationVerifier';
import Debug from 'debug';
import { ConfigurationChanged, ConfigurationCheckFailed, ConfigurationInjected, IOptionalConfig } from './deviceConfigurationActions';
import { fetch, FetchFn } from '../../Isomorphic/fetch';
import { call, put, takeEvery } from 'redux-saga/effects';
import { areTelemetryIntervalsSame, defaultTelemetryIntervals } from './telemetryIntervals';
import { defaultActionLimits } from './offlineActionsConfiguration';
import { defaultFeatureFlags } from './featureFlags';
import { SLOWLY as MIN_CHECK_INTERVAL_MS, SHORTLY, TIMEOUT, NORMALLY } from '../../Intervals/constants';
import { adjustSsl, getConfigurationUrl, GetConfigUrlFn } from './deviceConfigurationHelper';
import { isEqual } from 'lodash';
import { SocketName } from '../../Socket/socketSagas';
const debug = Debug('@signageos/front-display:Device:Configuration:deviceConfigurationChecker');

const NO_CONFIG_STATUS = 404;
const DEFAULT_CONFIG_PATH = '/configuration-default';

export type IOptionalDynamicConfig = IOptionalConfig | (IOptionalConfig & ISignedResponse);

interface IDynamicConfig extends IOptionalConfig<string | null> {}

interface IRequiredDynamicConfig extends Omit<IConfig, 'configUrls'>, IDynamicConfig {
	updatedAt: string;
	platformUri: string;
	socketDriver: string;
	staticBaseUrl: string;
	uploadBaseUrl: string;
	weinreUri: string;
	subscriptionType: SubscriptionType;
	checkInterval: number;
}

/**
 * @param defaultConfig fallback configuration elements
 * @param threadName debug log tag
 */
export function* checkDeviceConfigurationSagaManual(defaultConfig: IConfig, threadName: SocketName): Generator<any, any, any> {
	debug(threadName, 'Waiting for injected configuration');
	let lastConfig: IDynamicConfig = createDefaultConfig();
	yield takeEvery(ConfigurationInjected, function* (action: ConfigurationInjected) {
		const overrideConfig = action.config;
		const config: IRequiredDynamicConfig = yield mergeConfig(defaultConfig, overrideConfig);
		debug(threadName, 'Retrieved config', config, lastConfig);
		const isUpdated = isConfigUpdated(config, lastConfig);
		if (isUpdated) {
			debug(threadName, 'Config changed');
			yield put<ConfigurationChanged>({
				type: ConfigurationChanged,
				config,
			});
			lastConfig = config;
		}
	});
}

/**
 * @param deviceUid
 * @param defaultConfig
 * @param getConfigUrl
 * @param publicKey
 * @param fetchFn For tests only
 * @param threadName
 * @param minCheckInterval
 */
export function* checkDeviceConfigurationSaga(
	deviceUid: string,
	defaultConfig: IConfig,
	getConfigUrl: GetConfigUrlFn,
	publicKey: string,
	fetchFn: FetchFn = fetch,
	threadName: SocketName,
	/** Use different value in tests */
	minCheckInterval = MIN_CHECK_INTERVAL_MS,
): Generator<any, any, any> {
	let lastConfig: IDynamicConfig = createDefaultConfig();

	const errorProgressiveWait = createProgressiveWait(SHORTLY, 2, TIMEOUT);
	let tries: number = 0;

	while (true) {
		try {
			debug(threadName, 'Start checking', { tries });
			const configUrl = yield call(getConfigUrl);
			const noSsl = shouldSkipSsl(tries);
			const overrideConfig: IRequiredDynamicConfig = yield getOverrideConfig(deviceUid, configUrl, noSsl, publicKey, fetchFn, threadName);
			const config: IRequiredDynamicConfig = yield mergeConfig(defaultConfig, overrideConfig);
			debug(threadName, 'Retrieved config', config, lastConfig);
			errorProgressiveWait.reset(); // first retries are faster in case of fail
			const isUpdated = isConfigUpdated(config, lastConfig);
			if (isUpdated) {
				debug(threadName, 'Config changed');
				yield put<ConfigurationChanged>({
					type: ConfigurationChanged,
					config,
				});
				lastConfig = config;
			}
			tries = 0;
			yield wait(Math.max(config.checkInterval || 0, minCheckInterval));
		} catch (error) {
			debug(threadName, 'Checking erred', error);
			yield put<ConfigurationCheckFailed>({
				type: ConfigurationCheckFailed,
				error,
			});
			tries++;
			yield errorProgressiveWait.wait();
		}
	}
}

function createDefaultConfig(): IDynamicConfig {
	return {
		updatedAt: null,
		platformUri: null,
		socketDriver: null,
		staticBaseUrl: null,
		uploadBaseUrl: null,
		weinreUri: null,
		extendedManagementUrl: null,
		subscriptionType: null,
		checkInterval: null,
		telemetryIntervals: null,
		cacheInvalidationInterval: null,
		checkTimeBeforeConnection: false,
		offlineActionsLimits: null,
		featureFlags: null,
		screenshotRotationPortrait: null,
		telemetryInvalidatedAt: null,
	};
}

function isConfigUpdated(config: IRequiredDynamicConfig, lastConfig: IDynamicConfig) {
	return (
		config.updatedAt !== lastConfig.updatedAt ||
		config.platformUri !== lastConfig.platformUri ||
		config.socketDriver !== lastConfig.socketDriver ||
		config.staticBaseUrl !== lastConfig.staticBaseUrl ||
		config.uploadBaseUrl !== lastConfig.uploadBaseUrl ||
		config.subscriptionType !== lastConfig.subscriptionType ||
		config.checkInterval !== lastConfig.checkInterval ||
		config.checkTimeBeforeConnection !== lastConfig.checkTimeBeforeConnection ||
		!areTelemetryIntervalsSame(config.telemetryIntervals, lastConfig.telemetryIntervals) ||
		config.cacheInvalidationInterval !== lastConfig.cacheInvalidationInterval ||
		!isEqual(config.offlineActionsLimits, lastConfig.offlineActionsLimits) ||
		!isEqual(config.featureFlags, lastConfig.featureFlags) ||
		config.screenshotRotationPortrait !== lastConfig.screenshotRotationPortrait ||
		config.telemetryInvalidatedAt !== lastConfig.telemetryInvalidatedAt
	);
}

async function mergeConfig(defaultConfig: IConfig, overrideConfig: IOptionalDynamicConfig): Promise<IRequiredDynamicConfig> {
	return {
		updatedAt: overrideConfig.updatedAt,
		platformUri: overrideConfig.platformUri || defaultConfig.platformUri,
		socketDriver: overrideConfig.socketDriver || 'ws',
		staticBaseUrl: overrideConfig.staticBaseUrl || defaultConfig.staticBaseUrl,
		uploadBaseUrl: overrideConfig.uploadBaseUrl || defaultConfig.uploadBaseUrl,
		weinreUri: overrideConfig.weinreUri || defaultConfig.weinreUri,
		extendedManagementUrl: overrideConfig.extendedManagementUrl || defaultConfig.extendedManagementUrl,
		subscriptionType: overrideConfig.subscriptionType || defaultConfig.subscriptionType,
		checkInterval: overrideConfig.checkInterval || defaultConfig.checkInterval,
		telemetryIntervals: overrideConfig.telemetryIntervals || defaultConfig.telemetryIntervals || defaultTelemetryIntervals,
		cacheInvalidationInterval: overrideConfig.cacheInvalidationInterval || defaultConfig.cacheInvalidationInterval,
		checkTimeBeforeConnection: overrideConfig.checkTimeBeforeConnection || defaultConfig.checkTimeBeforeConnection,
		offlineActionsLimits: overrideConfig.offlineActionsLimits || defaultConfig.offlineActionsLimits || defaultActionLimits,
		featureFlags: overrideConfig.featureFlags || defaultConfig.featureFlags || defaultFeatureFlags,
		screenshotRotationPortrait: overrideConfig.screenshotRotationPortrait || defaultConfig.screenshotRotationPortrait,
		telemetryInvalidatedAt: overrideConfig.telemetryInvalidatedAt || defaultConfig.telemetryInvalidatedAt || null,
	};
}

async function getOverrideConfig(
	deviceUid: string,
	configUrl: string,
	noSsl: boolean,
	publicKey: string,
	fetchFn: FetchFn,
	threadName: SocketName,
): Promise<IOptionalDynamicConfig> {
	try {
		const resp = await fetchConfiguration(fetchFn, configUrl, noSsl, deviceUid, threadName);
		const config: IOptionalDynamicConfig = await resp.json();
		debug(threadName, 'Fetched config', config, { noSsl });
		if (noSsl && (!('signature' in config) || !isConfigResponseVerified(config, publicKey))) {
			throw new Error('Unverified message from server');
		}
		return {
			updatedAt: config.updatedAt,
			platformUri: config.platformUri,
			socketDriver: config.socketDriver,
			staticBaseUrl: config.staticBaseUrl,
			uploadBaseUrl: config.uploadBaseUrl,
			weinreUri: config.weinreUri,
			extendedManagementUrl: config.extendedManagementUrl,
			subscriptionType: config.subscriptionType,
			checkInterval: config.checkInterval,
			telemetryIntervals: config.telemetryIntervals,
			cacheInvalidationInterval: config.cacheInvalidationInterval,
			checkTimeBeforeConnection: config.checkTimeBeforeConnection,
			offlineActionsLimits: config.offlineActionsLimits,
			featureFlags: config.featureFlags,
			screenshotRotationPortrait: config.screenshotRotationPortrait,
			telemetryInvalidatedAt: config.telemetryInvalidatedAt,
		};
	} catch (error) {
		debug(threadName, 'Erred fetching config', error);
		if (error.request && error.request.status === 404 && typeof error.body === 'object') {
			return {
				updatedAt: error.body.updatedAt,
				platformUri: error.body.platformUri,
				socketDriver: error.body.socketDriver,
				staticBaseUrl: error.body.staticBaseUrl,
				uploadBaseUrl: error.body.uploadBaseUrl,
				weinreUri: error.body.weinreUri,
				extendedManagementUrl: error.body.extendedManagementUrl,
				subscriptionType: error.body.subscriptionType,
				checkInterval: error.body.checkInterval,
				telemetryIntervals: error.body.telemetryIntervals,
				cacheInvalidationInterval: error.body.cacheInvalidationInterval,
				checkTimeBeforeConnection: error.body.checkTimeBeforeConnection,
				offlineActionsLimits: error.body.offlineActionsLimits,
				featureFlags: error.body.featureFlags,
				screenshotRotationPortrait: error.body.screenshotRotationPortrait,
				telemetryInvalidatedAt: error.body.telemetryInvalidatedAt,
			};
		} else {
			throw error;
		}
	}
}

/**
 * Tries to fetch configuration from cached url.
 * @param fetchFn
 * @param configUrl
 * @param noSsl
 * @param deviceUid
 * @param threadName
 */
async function fetchConfiguration(fetchFn: FetchFn, configUrl: string, noSsl: boolean, deviceUid: string, threadName: SocketName) {
	const configurationUrl = getConfigurationUrl(configUrl, noSsl, deviceUid); // baseUrl is overridden in updatingConfiguration saga

	debug(threadName, 'Fetching config', configurationUrl, { noSsl });
	const configResponse = await fetchFn(configurationUrl, undefined, NORMALLY);
	if ((configResponse.ok || configResponse.status === NO_CONFIG_STATUS) && (await isResponseJson(configResponse))) {
		return configResponse;
	}

	const defaultConfigResponse = await fetchDefaultConfig(fetchFn, configUrl, noSsl);
	if (defaultConfigResponse.ok && (await isResponseJson(defaultConfigResponse))) {
		return defaultConfigResponse;
	}

	throw new Error(`Configuration is not available.`);
}

/**
 * Fetches default configuration from `/configuration-default`.
 * @param fetchFn
 * @param configUrl
 * @param noSsl
 */
async function fetchDefaultConfig(fetchFn: FetchFn, configUrl: string, noSsl: boolean) {
	const defaultConfigurationUrl = adjustSsl(`${configUrl}${DEFAULT_CONFIG_PATH}`, noSsl); // baseUrl is overridden in updatingConfiguration saga

	return fetchFn(defaultConfigurationUrl, undefined, NORMALLY);
}

/**
 * Returns `true` if response's body is valid JSON.
 * @param response
 */
async function isResponseJson(response: Response) {
	try {
		// throws if response is not JSON
		await response.clone().json();
		return true;
	} catch (e) {
		return false;
	}
}

function shouldSkipSsl(tries: number) {
	return (tries + 1) % 3 === 0; // Every third try is HTTP only (to try skip SSL errors)
}
